import React, { createContext, useContext, useMemo } from 'react';
import {
    AssetExplorerApi,
    DEFAULT_ASSET_EXPLORER_API_BASE_PATH,
    createAssetExplorerApi,
    defaultAssetExplorerApi,
} from './AssetExplorer.api';
import { ASSET_EXPLORER_UI_STATE_STORAGE_KEY } from './assetExplorerUiState';

/** Default localStorage key for the resizable content-tree width. */
export const ASSET_EXPLORER_SIDEBAR_WIDTH_STORAGE_KEY = 'assetExplorer.sidebarWidth';

/**
 * Public props of the shared `<AssetExplorer />`.
 *
 * Every prop is optional and defaults to the historical Asset Management behaviour, so the
 * component stays a drop-in for `/#/asset-management/asset-explorer` while `/#/beamonscreen/assets`
 * can point it at the Beam on Screen backend (`/bos`) with its own persisted UI state.
 */
export interface AssetExplorerProps {
    /** API route prefix on st-ck-server. `/ae` (Asset Management, default) or `/bos` (Beam on Screen). */
    apiBasePath?: string;
    /** Heading shown in the content-tree banner and the mobile accordion header. */
    title?: string;
    /** localStorage key for view mode / sort / filter / last folder. */
    uiStateStorageKey?: string;
    /** localStorage key for the desktop content-tree width. */
    sidebarWidthStorageKey?: string;
}

export interface AssetExplorerConfig {
    apiBasePath: string;
    api: AssetExplorerApi;
    title: string;
    uiStateStorageKey: string;
    sidebarWidthStorageKey: string;
}

export const DEFAULT_ASSET_EXPLORER_TITLE = 'Asset Explorer';

const defaultConfig: AssetExplorerConfig = {
    apiBasePath: DEFAULT_ASSET_EXPLORER_API_BASE_PATH,
    api: defaultAssetExplorerApi,
    title: DEFAULT_ASSET_EXPLORER_TITLE,
    uiStateStorageKey: ASSET_EXPLORER_UI_STATE_STORAGE_KEY,
    sidebarWidthStorageKey: ASSET_EXPLORER_SIDEBAR_WIDTH_STORAGE_KEY,
};

const AssetExplorerConfigContext = createContext<AssetExplorerConfig>(defaultConfig);

/** Resolves props → config (memoised so the API client instance is stable across renders). */
export const useResolvedAssetExplorerConfig = (props: AssetExplorerProps): AssetExplorerConfig => {
    const { apiBasePath, title, uiStateStorageKey, sidebarWidthStorageKey } = props;
    return useMemo<AssetExplorerConfig>(() => {
        const basePath = (apiBasePath || DEFAULT_ASSET_EXPLORER_API_BASE_PATH).trim();
        const api =
            basePath === DEFAULT_ASSET_EXPLORER_API_BASE_PATH
                ? defaultAssetExplorerApi
                : createAssetExplorerApi(basePath);
        return {
            apiBasePath: api.basePath,
            api,
            title: title?.trim() || DEFAULT_ASSET_EXPLORER_TITLE,
            uiStateStorageKey: uiStateStorageKey?.trim() || ASSET_EXPLORER_UI_STATE_STORAGE_KEY,
            sidebarWidthStorageKey: sidebarWidthStorageKey?.trim() || ASSET_EXPLORER_SIDEBAR_WIDTH_STORAGE_KEY,
        };
    }, [apiBasePath, title, uiStateStorageKey, sidebarWidthStorageKey]);
};

export const AssetExplorerConfigProvider: React.FC<{
    config: AssetExplorerConfig;
    children: React.ReactNode;
}> = ({ config, children }) => (
    <AssetExplorerConfigContext.Provider value={config}>{children}</AssetExplorerConfigContext.Provider>
);

/**
 * Explorer configuration for the current subtree.
 * Outside an `<AssetExplorer />` (e.g. Email builder published-asset picker) this falls back to `/ae`.
 */
export const useAssetExplorerConfig = (): AssetExplorerConfig => useContext(AssetExplorerConfigContext);

/** Route-prefix-bound REST client for the current explorer subtree. */
export const useAssetExplorerApi = (): AssetExplorerApi => useAssetExplorerConfig().api;
